import { useState } from 'react';
import { toast } from 'sonner';
import type { MaintenanceState } from '@reflex/shared';
import { Button } from '@reflex/ui/components/ui/button';
import { Switch } from '@reflex/ui/components/ui/switch';
import { Textarea } from '@reflex/ui/components/ui/textarea';
import { Label } from '@reflex/ui/components/ui/label';
import { SettingsRow } from '@reflex/ui/components/shared/SettingsRow';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { useMaintenanceState, useSetMaintenanceMutation } from '@/hooks/config/useMaintenanceAdmin';

const DESCRIPTION =
  'Show a persistent banner above the main content on every page. The app stays fully usable; the banner only clears when you turn maintenance mode off here.';

export function AdminMaintenancePage() {
  const maintenanceQuery = useMaintenanceState();
  const current = maintenanceQuery.data;

  return (
    <AdminSubPageLayout title="Maintenance" description={DESCRIPTION}>
      <AsyncContent
        isLoading={maintenanceQuery.isLoading}
        isError={maintenanceQuery.isError}
        loadingMessage="Loading maintenance settings…"
        errorMessage="Failed to load maintenance settings."
      >
        {current && (
          // Re-key on the loaded values so a background refetch that changes
          // them re-seeds the form via the child's useState initializers,
          // avoiding a setState-in-effect.
          <MaintenanceForm key={`${current.enabled}:${current.message}`} initial={current} />
        )}
      </AsyncContent>
    </AdminSubPageLayout>
  );
}

function MaintenanceForm({ initial }: { initial: MaintenanceState }) {
  const setMaintenance = useSetMaintenanceMutation();
  const [enabled, setEnabled] = useState(initial.enabled);
  const [message, setMessage] = useState(initial.message);

  const dirty = enabled !== initial.enabled || message.trim() !== initial.message.trim();
  const messageMissing = enabled && message.trim().length === 0;

  async function handleSave() {
    if (messageMissing) return;
    await setMaintenance.mutateAsync(
      { enabled, message: message.trim() },
      {
        onSuccess: () => toast.success('Maintenance mode saved'),
        onError: (err) =>
          toast.error('Failed to save maintenance mode', { description: err.message }),
      },
    );
  }

  return (
    <div className="space-y-6">
      <SettingsRow
        title="Maintenance mode"
        description="When on, every user sees the banner below until you turn this off."
        control={
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            aria-label="Toggle maintenance mode"
          />
        }
      />

      <div className="space-y-2">
        <Label htmlFor="maintenance-message">Banner message</Label>
        <Textarea
          id="maintenance-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="We're performing scheduled maintenance. Some features may be slow."
          rows={3}
        />
        {messageMissing ? (
          <PageDescription className="mt-0 text-xs text-status-error">
            Add a message so users know what's going on.
          </PageDescription>
        ) : (
          <PageDescription className="mt-0 text-xs">
            Shown verbatim in the banner. Line breaks are preserved.
          </PageDescription>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!dirty || messageMissing || setMaintenance.isPending}
        >
          {setMaintenance.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
